import { UserProfile, UserAccountType } from "../types/userProfile";
import { saveUserProfileFirestore, loadUserProfileFirestore, auth } from "./firebase";
import { StorageService, STORAGE_KEYS, UserProfileRepository } from "../services/storage";

export { DEFAULT_USER_PROFILE } from "../services/storage/repositories/UserProfileRepository";
import { DEFAULT_USER_PROFILE } from "../services/storage/repositories/UserProfileRepository";
import { PLAN_PDV_VENDAS_LOCAIS_ID } from "./commissionConfig";

export function ensureDefaultOwnerPDVPlan(profile: UserProfile, now: number = Date.now()): UserProfile {
  if (profile.accountType !== "dono_arena") {
    return profile;
  }

  const isExplicitPaid =
    profile.isOperationOnly === true ||
    profile.financialModel === "OPERATION_ONLY" ||
    profile.financialModel === "FIXED_MONTHLY" ||
    profile.financialModel === "HYBRID" ||
    profile.arenaFinancialModel === "OPERATION_ONLY" ||
    profile.arenaFinancialModel === "FIXED_MONTHLY" ||
    profile.arenaFinancialModel === "HYBRID";

  if (isExplicitPaid) {
    return profile;
  }

  const startAt = typeof profile.trialStartAt === "number" && profile.trialStartAt > 0
    ? profile.trialStartAt
    : (typeof profile.createdAt === "number" && profile.createdAt > 0 ? profile.createdAt : now);
  const endAt = typeof profile.trialEndAt === "number" && profile.trialEndAt > 0
    ? profile.trialEndAt
    : startAt + 30 * 24 * 60 * 60 * 1000;
  const isExpired = now >= endAt;

  return {
    ...profile,
    commercialPlanId: profile.commercialPlanId || PLAN_PDV_VENDAS_LOCAIS_ID,
    commercialPlanName: profile.commercialPlanName || "PDV / VENDAS LOCAIS",
    financialModel: profile.financialModel || "PDV_VENDAS_LOCAIS",
    arenaFinancialModel: (profile.arenaFinancialModel || "PDV_VENDAS_LOCAIS") as any,
    trialStartAt: startAt,
    trialEndAt: endAt,
    trialStatus: isExpired ? "TRIAL_EXPIRED" : "TRIAL_ACTIVE",
    commercialStatus: isExpired ? "PDV_EXPIRED" : "PDV_ACTIVE",
    isPDVPlan: true,
    financialConfig: {
      arenaId: profile.arenaId || "",
      financialModel: (profile.financialConfig?.financialModel || "PDV_VENDAS_LOCAIS") as any,
      commercialPlanId: profile.financialConfig?.commercialPlanId || PLAN_PDV_VENDAS_LOCAIS_ID,
      trialStartAt: startAt,
      trialEndAt: endAt,
      trialStatus: isExpired ? "TRIAL_EXPIRED" : "TRIAL_ACTIVE",
      commercialStatus: isExpired ? "PDV_EXPIRED" : "PDV_ACTIVE",
      ...(profile.financialConfig || {}),
    },
  };
}

function getStorageKeyForUser(userKey?: string): string {
  return UserProfileRepository.getStorageKeyForUser(userKey);
}

export function loadUserProfile(userKey?: string): UserProfile {
  try {
    // 1. Se userKey foi explicitamente informado (ID ou Email), busca APENAS por aquela chave
    if (userKey && userKey !== "guest_visitor") {
      const parsed = UserProfileRepository.getProfileByKey(userKey);
      if (parsed && parsed.id !== "guest_visitor") {
        return sanitizeProfile(parsed);
      }
      // REGRA DE ISOLAMENTO: Quando userKey é informado e não encontrado,
      // NUNCA consultar getActiveProfile() como fallback para não vazar perfil de outro usuário.
      return DEFAULT_USER_PROFILE;
    }

    // 2. Sem userKey explícito (apenas em inicialização anônima/global): Tenta perfil ativo seguro
    const parsedActive = UserProfileRepository.getActiveProfile();
    if (parsedActive && parsedActive.id !== "guest_visitor") {
      return sanitizeProfile(parsedActive);
    }
  } catch (error) {
    console.error("Erro ao carregar perfil:", error);
  }
  return DEFAULT_USER_PROFILE;
}

function sanitizeProfile(parsed: any): UserProfile {
  let accountType: UserAccountType = "atleta";
  if (parsed.accountType === "dono_arena" || parsed.roleMode === "dono_arena") {
    accountType = "dono_arena";
  }

  const isAthlete = accountType === "atleta";

  let sanitized: UserProfile = {
    ...DEFAULT_USER_PROFILE,
    ...parsed,
    accountType,
    isArenaOwner: isAthlete ? false : parsed.isArenaOwner,
    arenaId: isAthlete ? undefined : parsed.arenaId,
    ownerUid: isAthlete ? undefined : parsed.ownerUid,
    arenaName: isAthlete ? "" : (parsed.arenaName || ""),
  };

  if (sanitized.accountType === "dono_arena") {
    sanitized = ensureDefaultOwnerPDVPlan(sanitized);
  }

  return sanitized;
}

export function saveUserProfile(profile: UserProfile): void {
  try {
    if (!profile) return;

    let finalProfile = { ...profile };

    // Se o usuário estiver autenticado com Firebase Auth, o ID DEVE ser o UID do Firebase Auth
    if (auth?.currentUser?.uid) {
      finalProfile.id = auth.currentUser.uid;
      if (auth.currentUser.email && !finalProfile.email) {
        finalProfile.email = auth.currentUser.email;
      }
    }

    // Se ainda for guest_visitor mas tiver nome ou dados, gera um ID permanente de usuário
    if (!finalProfile.id || finalProfile.id === "guest_visitor") {
      finalProfile.id = `user_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    }

    // Carrega o perfil anterior gravado (pelo ID ou pelo Email) para NUNCA apagar campos que já estavam preenchidos
    let existingProfile: UserProfile | null = null;
    if (finalProfile.id && finalProfile.id !== "guest_visitor") {
      const p = loadUserProfile(finalProfile.id);
      if (p && p.id !== "guest_visitor" && p.id === finalProfile.id) existingProfile = p;
    }
    if ((!existingProfile || existingProfile.id === "guest_visitor") && finalProfile.email) {
      const p = loadUserProfile(finalProfile.email);
      if (p && p.id !== "guest_visitor" && (p.email?.toLowerCase() === finalProfile.email.toLowerCase() || p.id === finalProfile.id)) {
        existingProfile = p;
      }
    }

    if (existingProfile && existingProfile.id !== "guest_visitor") {
      const isAthlete = finalProfile.accountType === "atleta";
      finalProfile = {
        ...DEFAULT_USER_PROFILE,
        ...existingProfile,
        ...finalProfile,
        // Preserva valores anteriores se os novos vierem vazios ou indefinidos
        cpf: finalProfile.cpf || existingProfile.cpf || "",
        cnpj: finalProfile.cnpj || existingProfile.cnpj || "",
        phone: finalProfile.phone || existingProfile.phone || "",
        city: finalProfile.city || existingProfile.city || "",
        state: finalProfile.state || existingProfile.state || "",
        pixKey: finalProfile.pixKey !== undefined ? finalProfile.pixKey : (existingProfile.pixKey || ""),
        pixKeyType: finalProfile.pixKeyType || existingProfile.pixKeyType || "",
        pixAddressKey: finalProfile.pixAddressKey !== undefined ? finalProfile.pixAddressKey : (existingProfile.pixAddressKey || ""),
        pixAddressKeyType: finalProfile.pixAddressKeyType || existingProfile.pixAddressKeyType || "",
        pixKeyValidated: finalProfile.pixKeyValidated !== undefined ? finalProfile.pixKeyValidated : existingProfile.pixKeyValidated,
        pixKeyValidatedAt: finalProfile.pixKeyValidatedAt || existingProfile.pixKeyValidatedAt || undefined,
        ownerPixKey: isAthlete ? "" : (finalProfile.ownerPixKey !== undefined ? finalProfile.ownerPixKey : (existingProfile.ownerPixKey || "")),
        ownerPixKeyType: isAthlete ? "" : (finalProfile.ownerPixKeyType || existingProfile.ownerPixKeyType || ""),
        arenaName: isAthlete ? "" : (finalProfile.arenaName || existingProfile.arenaName || ""),
        street: finalProfile.street || existingProfile.street || "",
        number: finalProfile.number || existingProfile.number || "",
        neighborhood: finalProfile.neighborhood || existingProfile.neighborhood || "",
        complement: finalProfile.complement || existingProfile.complement || "",
        preferredSports: (finalProfile.preferredSports && finalProfile.preferredSports.length > 0) ? finalProfile.preferredSports : (existingProfile.preferredSports || ["Beach Tennis", "Futevôlei"]),
      };
    }

    if (finalProfile.accountType === "atleta") {
      (finalProfile as any).isArenaOwner = false;
      finalProfile.arenaId = undefined;
      (finalProfile as any).ownerUid = undefined;
      finalProfile.arenaName = "";
    } else if (finalProfile.accountType === "dono_arena") {
      finalProfile = ensureDefaultOwnerPDVPlan(finalProfile);
    }

    // Salva na chave da sessão ativa
    UserProfileRepository.setActiveProfile(finalProfile);

    // Salva em chave isolada por ID e por Email do usuário no localStorage
    if (finalProfile.id) {
      UserProfileRepository.setProfileByKey(finalProfile.id, finalProfile);
    }
    if (finalProfile.email) {
      UserProfileRepository.setProfileByKey(finalProfile.email, finalProfile);
    }

    // Salva no Firestore sob users/{currentUser.uid} e users_by_email/{cleanEmail}
    saveUserProfileFirestore(finalProfile).catch((e) => {
      console.error("Erro ao sincronizar perfil com Firestore:", e);
    });
  } catch (error) {
    console.error("Erro ao salvar perfil:", error);
  }
}

export function clearUserProfileOnLogout(): UserProfile {
  try {
    UserProfileRepository.clearProfilesOnLogout();

    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem("app_arena_courts_v7");
      window.localStorage.removeItem("playarena_courts");
      window.localStorage.removeItem("app_arena_cpf_list");
      window.localStorage.removeItem("app_user_profile_active");
      window.localStorage.removeItem("app_user_profile");
    }

    // Limpa o sessionStorage completamente se disponível
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.clear();
    }
  } catch (error) {
    console.error("Erro ao limpar perfil no logout:", error);
  }
  return DEFAULT_USER_PROFILE;
}

export function isProfileIncomplete(profile: UserProfile): boolean {
  if (!profile || profile.id === "guest_visitor" || profile.id.includes("guest_")) {
    return true;
  }
  if (!profile.name || !profile.name.trim() || profile.name.toLowerCase().includes("visitante") || profile.name === "Usuário Google") {
    return true;
  }
  if (!profile.city || !profile.city.trim() || !profile.state || !profile.state.trim()) {
    return true;
  }
  if (!profile.phone || !profile.phone.trim()) {
    return true;
  }
  if (profile.accountType === "dono_arena") {
    if (!profile.arenaName || !profile.arenaName.trim() || !profile.cpf || !profile.cpf.trim()) {
      return true;
    }
  }
  return false;
}

export function isProfileComplete(profile: UserProfile): boolean {
  return !isProfileIncomplete(profile);
}

export async function findExistingUserProfile(userKeyOrEmailOrUid: string): Promise<UserProfile | null> {
  if (!userKeyOrEmailOrUid || userKeyOrEmailOrUid === "guest_visitor") return null;

  // 1. Procura no localStorage por ID ou E-mail (estritamente associado à chave)
  const localProf = loadUserProfile(userKeyOrEmailOrUid);
  const isKeyMatched = Boolean(
    localProf &&
    localProf.id !== "guest_visitor" &&
    !localProf.id.startsWith("guest_") &&
    (localProf.id === userKeyOrEmailOrUid || (userKeyOrEmailOrUid.includes("@") && localProf.email?.toLowerCase() === userKeyOrEmailOrUid.toLowerCase()))
  );

  if (
    isKeyMatched && 
    (
      (localProf.name && !localProf.name.toLowerCase().includes("visitante") && localProf.name !== "Usuário Google") || 
      localProf.phone || 
      localProf.cpf || 
      localProf.arenaName
    )
  ) {
    return localProf;
  }

  // 2. Busca no Firestore (por UID ou por Email)
  try {
    const cloudProf = await loadUserProfileFirestore(userKeyOrEmailOrUid);
    if (cloudProf && cloudProf.id !== "guest_visitor") {
      saveUserProfile(cloudProf);
      return cloudProf;
    }
  } catch (err) {
    console.error("Erro ao buscar perfil existente no Firestore:", err);
  }

  return null;
}
